import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Add as AddIcon } from '@mui/icons-material';
import Fab from '@mui/material/Fab';
import * as dates from '@simplybenefits/sb-dates';
import {
    TextInput,
    Services,
    Text,
    Modal,
    Theme,
    DateInput
} from '@simplybenefits/sb-ui-library';
import PersonBlock from '../../components/PersonBlock.js';
import { addTrustee, editTrustee, removeTrustee } from '../../actions/users.js';
import PermissionsComponent from '../../components/PermissionsComponent.js';
import BeneficiaryMessageCard from '../../components/MessageCards/BeneficiaryMessageCard.js';

class Trustee extends PureComponent {
    state = {
        firstname: '',
        lastname: '',
        relationship: '',
        trustee: null,
        dialogOpen: false,
        isEdit: false,
        trusteeID: null,
        options: Services.selectFieldOptions.optionsWithSpouse(),
        trusteeToRemoveID: false,
        skipTrustee: false,
        showEffectiveDateDialog: false,
        effectiveDate: dates.getUTC(null, 'YYYY/MM/DD')
    };

    componentDidMount() {
        this.props.primaryBeneficiaries && this.props.primaryBeneficiaries.map((beneficiary) => {
            const age = this._getAge(beneficiary.birthdate * 1000);

            if (age >= 18) {
                this.setState({
                    skipTrustee: true
                });
            } else if (age < 18) {
                this.setState({
                    skipTrustee: false
                });
            }

            return null;
        });
    }

    _getAge = (birthdate) => {
        birthdate = dates.getUTC(birthdate);
        return Math.floor((dates.getUTC() - birthdate) / (60 * 60 * 24 * 365));
    };

    _close = () => {
        this.setState({
            firstnameError: null,
            lastnameError: null,
            relationshipError: null,
            dialogOpen: false,
            isEdit: false,
            trusteeToRemoveID: false,
            showEffectiveDateDialog: false,
            effectiveDate: dates.getUTC(null, 'YYYY/MM/DD')
        });
    };

    _onBlur = async (type) => {
        this.setState({
            [`${ type }Error`]: await Services.validateInput(type, this.state[type])
        });
    };

    _handleInputChange = (type, event) => {
        this.setState({
            [type]: event.target.value
        });
    };

    _validateForm = async () => {
        const firstnameError = await Services.validateInput('firstname', this.state.firstname);
        const lastnameError = await Services.validateInput('lastname', this.state.lastname);
        const relationshipError = await Services.validateInput('relationship', this.state.relationship);

        this.setState({
            firstnameError,
            lastnameError,
            relationshipError
        });

        if (
            firstnameError ||
            lastnameError ||
            relationshipError
        ) {
            return false;
        }

        return true;
    };

    _add = () => {
        this.setState({
            firstnameError: false,
            lastnameError: false,
            sharesError: false,
            firstname: '',
            lastname: '',
            relationship: '',
            isEdit: false,
            trusteeToRemoveID: false,
            dialogOpen: true
        });
    };

    _edit = async (trustee) => {
        await this.setState({
            trusteeID: trustee.trusteeID,
            firstname: trustee.firstname,
            lastname: trustee.lastname,
            relationship: trustee.relationship,
            isEdit: true,
            dialogOpen: true,
            ageDisclaimer: false,
            trusteeToRemoveID: false
        });
    };

    _setIDForRemoval = (trusteeToRemoveID) => {
        this.setState({
            dialogOpen: false,
            trusteeToRemoveID
        });

        this._openEffectiveDateDialog('remove');
    };

    /**
     * Called on save trustee
     * @private
     */
    _save = async () => {
        const { firstname, lastname, relationship, trusteeID } = this.state;
        let success = false;

        const data = {
            firstname,
            lastname,
            relationship,
            effectiveDate: dates.getUTC(this.state.effectiveDate)
        };

        if (this.state.isEdit) {
            success = await this.props.dispatch(editTrustee(this.props.user.userID, trusteeID, data));
        } else {
            success = await this.props.dispatch(addTrustee(this.props.user.userID, data));
        }

        if (success) {
            this.setState({
                isEdit: false,
                dialogOpen: false,
                showEffectiveDateDialog: false
            });

            return true;
        }

        // reset on error to the dialog form view
        this.setState({
            showEffectiveDateDialog: false,
            dialogOpen: true
        });

        return false;
    };

    _remove = async () => {
        const success = await this.props.dispatch(removeTrustee(
            this.props.user.userID,
            this.state.trusteeToRemoveID,
            dates.getUTC(this.state.effectiveDate)
        ));

        if (success) {
            this.setState({
                trusteeToRemoveID: false,
                dialogOpen: false
            });
        }

        return null;
    };

    _openEffectiveDateDialog = async (type) => {
        if (type !== 'remove') {
            const isFormValid = await this._validateForm();

            if (!isFormValid) {
                return null;
            }
        }

        // if the account is not yet setup then we don't need to ask for an effective date
        if (this.props.meta.accountEffDate === null) {
            if (type === 'remove') {
                return this._remove();
            }

            return this._save();
        }


        this.setState({
            dialogOpen: false,
            showEffectiveDateDialog: true
        });

        return null;
    };

    _closeEffectiveDateDialog = () => {
        this.setState({
            showEffectiveDateDialog: false
        });
    };

    render() {
        const { trustee } = this.props;

        return (
            <div>
                <BeneficiaryMessageCard user={ this.props.user } />
                {
                    (!trustee || trustee.firstname === null) ?
                        this.state.skipTrustee ?
                            <Text
                                testid="trustee"
                                light
                                medium
                                align="center"
                            >
                                No Trustee is required because they have no beneficiaries under the age of 18.
                            </Text>
                            :
                            <Text
                                testid="trustee"
                                light
                                medium
                                align="center"
                            >
                                The user has not defined a trustee.
                            </Text>
                        :
                        <PersonBlock
                            testid="trustee"
                            key={trustee.trusteeID}
                            firstname={trustee.firstname}
                            lastname={trustee.lastname}
                            relationship={trustee.relationship}
                            dateAdded={trustee.dateAdded}
                            parentID={trustee.parentID}
                            type="trustee"
                            edit={() => this._edit(trustee)}
                            remove={() => this._setIDForRemoval(trustee.trusteeID)}
                        />
                }

                <PermissionsComponent
                    permission="Trustee"
                    accessType={this.props.accessType}
                    level="write"
                >
                    {
                        this.props.trustee === null &&
                        <Fab
                            id="add-trustee-fab"
                            aria-label="Add"
                            onClick={this._add}
                            style={{
                                backgroundColor: Theme.colors.brandGreen,
                                position: 'fixed',
                                bottom: '5%',
                                right: '5%',
                                zIndex: 500
                            }}
                        >
                            <AddIcon style={{ color: Theme.colors.surface }} />
                        </Fab>
                    }
                </PermissionsComponent>

                <Modal
                    testid="add-edit-trustee-modal"
                    open={this.state.dialogOpen}
                    onClose={this._close}
                    title={ this.state.isEdit ? 'Edit Trustee' : 'Add Trustee' }
                    primaryButton={{
                        onClick: () => this._openEffectiveDateDialog('save')
                    }}
                    loader={this.props.loader}
                >
                    <React.Fragment>
                        <TextInput
                            label="First Name"
                            value={this.state.firstname}
                            onBlur={() => this._onBlur('firstname')}
                            onChange={(value) => this._handleInputChange('firstname', value)}
                            error={Boolean(this.state.firstnameError)}
                            helperText={this.state.firstnameError}
                            fullWidth
                            autoFocus
                        />

                        <TextInput
                            label="Last Name"
                            value={this.state.lastname}
                            onBlur={() => this._onBlur('lastname')}
                            onChange={(value) => this._handleInputChange('lastname', value)}
                            error={Boolean(this.state.lastnameError)}
                            helperText={this.state.lastnameError}
                            fullWidth
                        />

                        <TextInput
                            label="Relationship"
                            value={this.state.relationship}
                            fullWidth
                            onBlur={() => this._onBlur('relationship')}
                            onChange={(value) => this._handleInputChange('relationship', value)}
                            error={Boolean(this.state.relationshipError)}
                            helperText={this.state.relationshipError}
                            select
                            selectOptions={this.state.options}
                        />
                    </React.Fragment>
                </Modal>

                <Modal
                    open={this.state.showEffectiveDateDialog}
                    transitionComponent={this._transition}
                    onClose={this._closeEffectiveDateDialog}
                    title="Set Effective Date"
                    primaryButton={{
                        label: "Submit",
                        onClick: this.state.trusteeToRemoveID ? this._remove : this._save
                    }}
                    loader={this.props.loader}
                >
                    <Text medium>
                        Please provide the date you would like the change(s) to be effective:
                    </Text>

                    <DateInput
                        label="Effective Date"
                        value={this.state.effectiveDate}
                        onBlur={() => this._onBlur('effectiveDate')}
                        onChange={(e) => this._handleInputChange('effectiveDate', e)}
                        error={Boolean(this.state.effectiveDateError)}
                        helperText={this.state.effectiveDateError}
                        fullWidth
                        required
                    />

                    <Text medium margin="10px 0 0 0">
                        By clicking Submit below, the changes made will be applied and effective on the date provided above. Back dated changes will have adjustments applied using current rates.
                    </Text>
                </Modal>

                <span id="user-trustee" />
            </div>
        );
    }
}

Trustee.propTypes = {
    trustee: PropTypes.object,
    primaryBeneficiaries: PropTypes.array,
    accessType: PropTypes.string
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    trustee: state.users.currentUser.trustee,
    primaryBeneficiaries: state.users.currentUser.beneficiaries.primary,
    user: state.users.currentUser.meta,
    accessType: state.master.access
});

export default connect(mapStateToProps)(Trustee);